import { takeLatest, put, all, call } from 'redux-saga/effects';
import { wellnessAPI } from 'resources/api';
import { processError } from 'state/utils';
import Types from './types';

// ─── MOOD ────────────────────────────────────────────────────────────────────

function* saveMoodAsync({ payload }) {
    try {
        const res = yield wellnessAPI.saveMood(payload);
        yield put({ type: Types.SAVE_MOOD_SUCCESS, payload: res.data || res });
        // Refresh history after saving
        yield put({ type: Types.FETCH_MOOD_HISTORY_START, payload: { days: 7 } });
    } catch (error) {
        const message = processError(error);
        yield put({ type: Types.SAVE_MOOD_ERROR, payload: message });
    }
}

function* saveMoodStart() {
    yield takeLatest(Types.SAVE_MOOD_START, saveMoodAsync);
}

function* fetchMoodHistoryAsync({ payload }) {
    try {
        const { days = 7 } = payload;
        const res = yield wellnessAPI.getMoodHistory(days);
        // Extract today's mood from history
        const history = res.data || [];
        const today = new Date().toISOString().slice(0, 10);
        const todayEntry = history.find(
            (m) => m.date && new Date(m.date).toISOString().slice(0, 10) === today
        );
        yield put({
            type: Types.FETCH_MOOD_HISTORY_SUCCESS,
            payload: { history, today: todayEntry || null },
        });
    } catch (error) {
        const message = processError(error);
        yield put({ type: Types.FETCH_MOOD_HISTORY_ERROR, payload: message });
    }
}

function* fetchMoodHistoryStart() {
    yield takeLatest(Types.FETCH_MOOD_HISTORY_START, fetchMoodHistoryAsync);
}

// ─── JOURNAL ─────────────────────────────────────────────────────────────────

function* saveJournalAsync({ payload }) {
    try {
        const res = yield wellnessAPI.saveJournal(payload);
        yield put({ type: Types.SAVE_JOURNAL_SUCCESS, payload: res.data || res });
    } catch (error) {
        const message = processError(error);
        yield put({ type: Types.SAVE_JOURNAL_ERROR, payload: message });
    }
}

function* saveJournalStart() {
    yield takeLatest(Types.SAVE_JOURNAL_START, saveJournalAsync);
}

function* fetchJournalAsync({ payload }) {
    try {
        const { days = 30 } = payload;
        const res = yield wellnessAPI.getJournal(days);
        yield put({ type: Types.FETCH_JOURNAL_SUCCESS, payload: res.data || [] });
    } catch (error) {
        const message = processError(error);
        yield put({ type: Types.FETCH_JOURNAL_ERROR, payload: message });
    }
}

function* fetchJournalStart() {
    yield takeLatest(Types.FETCH_JOURNAL_START, fetchJournalAsync);
}

// ─── SOS ─────────────────────────────────────────────────────────────────────

function* logSosAsync({ payload }) {
    try {
        yield wellnessAPI.logSos(payload);
        yield put({ type: Types.LOG_SOS_SUCCESS });
    } catch (error) {
        // Best-effort: no bloquear la acción SOS por fallo de red
        yield put({ type: Types.LOG_SOS_ERROR, payload: processError(error) });
    }
}

function* logSosStart() {
    yield takeLatest(Types.LOG_SOS_START, logSosAsync);
}

// ─── ACTIVITY ────────────────────────────────────────────────────────────────

function* saveActivityAsync({ payload }) {
    try {
        yield wellnessAPI.saveActivity(payload);
        yield put({ type: Types.SAVE_ACTIVITY_SUCCESS });
    } catch (error) {
        yield put({ type: Types.SAVE_ACTIVITY_ERROR, payload: processError(error) });
    }
}

function* saveActivityStart() {
    yield takeLatest(Types.SAVE_ACTIVITY_START, saveActivityAsync);
}

// ─── ALERTS (therapist) ──────────────────────────────────────────────────────

function* fetchAlertsAsync() {
    try {
        const res = yield wellnessAPI.getAlerts();
        yield put({ type: Types.FETCH_ALERTS_SUCCESS, payload: res?.data || res });
    } catch (error) {
        yield put({ type: Types.FETCH_ALERTS_ERROR, payload: processError(error) });
    }
}

function* fetchAlertsStart() {
    yield takeLatest(Types.FETCH_ALERTS_START, fetchAlertsAsync);
}

// ─── PATIENT SUMMARY (therapist) ────────────────────────────────────────────

function* fetchPatientSummaryAsync({ payload }) {
    try {
        const { clientId, days = 30 } = payload;
        const res = yield wellnessAPI.getPatientSummary(clientId, days);
        yield put({ type: Types.FETCH_PATIENT_SUMMARY_SUCCESS, payload: res?.data || res });
    } catch (error) {
        yield put({ type: Types.FETCH_PATIENT_SUMMARY_ERROR, payload: processError(error) });
    }
}

function* fetchPatientSummaryStart() {
    yield takeLatest(Types.FETCH_PATIENT_SUMMARY_START, fetchPatientSummaryAsync);
}

// ─── ROOT ────────────────────────────────────────────────────────────────────

export default function* wellnessSagas() {
    yield all([
        call(saveMoodStart),
        call(fetchMoodHistoryStart),
        call(saveJournalStart),
        call(fetchJournalStart),
        call(logSosStart),
        call(saveActivityStart),
        call(fetchAlertsStart),
        call(fetchPatientSummaryStart),
    ]);
}
